/**
 * CourseCustomFieldsSection
 *
 * "Custom Fields" card for the Settings tab. Renders one row per defined
 * field with a type-appropriate value input (text / number / multi-select).
 *
 * Field DEFINITIONS are account-scoped reference data (the lookups slice),
 * so adding one here makes it appear on every course's edit page. Per-course
 * VALUES live in the parent-owned `values` map and persist via the course's
 * own save flow.
 *
 * The "Manage fields" button opens a schema editor where admins can rename
 * fields, manage select options, or delete fields.
 */

import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CourseCustomField,
  CourseCustomFieldType,
  SaveCourseCustomFieldRequest,
} from '../../apis/api-client';
import {
  createCustomField,
  selectAllCustomFields,
  updateCustomField,
  useEnsureCustomFields,
} from '../../features/lookups/lookupsSlice';
import { useAppDispatch } from '../../stores';
import { Box, Button, IconButton, MenuItem, Select, TextField, Typography } from '../ui';
import { AddIcon, CloseIcon, SettingsIcon } from '../icons';
import CustomFieldValueInput from './CustomFieldValueInput';
import ManageCustomFieldsDialog from './ManageCustomFieldsDialog';

export interface CourseCustomFieldsSectionProps {
  /** Map of fieldId → value (string[]), owned by the parent so the form's
   *  dirty tracking and save flow stay centralized. */
  values: Record<number, string[]>;
  onChange: (next: Record<number, string[]>) => void;
}

export default function CourseCustomFieldsSection({ values, onChange }: CourseCustomFieldsSectionProps) {
  const dispatch = useAppDispatch();
  useEnsureCustomFields();
  const definitions = useSelector(selectAllCustomFields);

  // Inline "add field" form state.
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<CourseCustomFieldType>(CourseCustomFieldType.Text);
  const [newNameError, setNewNameError] = useState('');

  // Schema management dialog.
  const [manageOpen, setManageOpen] = useState(false);

  const handleAddField = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setNewNameError('Field name is required');
      return;
    }
    const existing = definitions.find((f) => (f.name ?? '').toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      setNewNameError('A field with this name already exists');
      return;
    }
    const request: SaveCourseCustomFieldRequest = {
      name: trimmed,
      fieldType: newType,
      options: newType === CourseCustomFieldType.Select ? [] : undefined,
      sequenceOrder: definitions.length,
    };
    await dispatch(createCustomField(request));
    setNewName('');
    setNewType(CourseCustomFieldType.Text);
    setNewNameError('');
    setAdding(false);
  };

  const handleValueChange = (def: CourseCustomField, raw: string[]) => {
    const next = { ...values };
    if (raw.length === 0) {
      delete next[def.id!];
    } else {
      next[def.id!] = raw;
    }
    onChange(next);
  };

  // Persist a newly typed Select option onto the field definition so it
  // appears in every course's picker for the same field.
  const handleAddOption = (def: CourseCustomField, option: string) => {
    const trimmed = option.trim();
    if (!trimmed || !def.id) return;
    const opts = def.options ?? [];
    if (opts.some((o) => o.toLowerCase() === trimmed.toLowerCase())) return;
    dispatch(
      updateCustomField({
        id: def.id,
        request: {
          name: def.name,
          fieldType: def.fieldType,
          options: [...opts, trimmed],
          sequenceOrder: def.sequenceOrder,
        },
      }),
    );
  };

  const newFieldHint =
    newType === CourseCustomFieldType.Select
      ? 'Multi-select field — pick from a curated list of options per course. Type to add new options that become available on every course.'
      : newType === CourseCustomFieldType.Number
      ? 'Numeric field — each course holds a single number.'
      : 'Text field — each course holds a single free-text value.';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {definitions.length === 0 && !adding && (
        <Typography variant="body2" color="text.secondary">
          No custom fields yet. Add one to capture attributes like CE hours,
          professional role, or category. <strong>Fields you add here apply to
          every course</strong> — set this course's value once it's defined.
        </Typography>
      )}

      {definitions.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 2,
          }}
        >
          {definitions.map((def) => (
            <CustomFieldValueInput
              key={def.id}
              field={def}
              value={values[def.id!] ?? []}
              onChange={(next) => handleValueChange(def, next)}
              onAddOption={(option) => handleAddOption(def, option)}
            />
          ))}
        </Box>
      )}

      {adding ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              label="Field name"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                if (newNameError) setNewNameError('');
              }}
              placeholder="e.g. CE Hours, Professional Role, Category"
              error={!!newNameError}
              helperText={newNameError}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
            />
            <Select
              value={newType}
              onChange={(e) => setNewType(Number(e.target.value) as CourseCustomFieldType)}
              size="small"
              sx={{ minWidth: 160 }}
            >
              <MenuItem value={CourseCustomFieldType.Text}>Text</MenuItem>
              <MenuItem value={CourseCustomFieldType.Number}>Number</MenuItem>
              <MenuItem value={CourseCustomFieldType.Select}>Multi-select</MenuItem>
            </Select>
            <Button variant="contained" color="primary" size="small" onClick={handleAddField} sx={{ mt: 0.25 }}>
              Add
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                setAdding(false);
                setNewName('');
                setNewNameError('');
              }}
              sx={{ mt: 0.25 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {newFieldHint}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
            sx={{
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.23)',
              '&:hover': { borderColor: 'rgba(0, 0, 0, 0.5)', bgcolor: 'action.hover' },
            }}
          >
            Add custom field
          </Button>
          {definitions.length > 0 && (
            <Button
              variant="text"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => setManageOpen(true)}
            >
              Manage fields
            </Button>
          )}
        </Box>
      )}

      <ManageCustomFieldsDialog open={manageOpen} onClose={() => setManageOpen(false)} />
    </Box>
  );
}
